import $ from 'jquery'

function isSuperset(set, subset) {
  for (const elem of subset) {
    if (!set.has(elem)) {
      return false
    }
  }

  return true
}

function charsOfText(text) {
  return new Set(text.replace(/\s/g, ''))
}

function textOfChars(chars) {
  return [...chars].join('')
}

function encodeChars(chars) {
  return encodeURIComponent(textOfChars(chars))
}

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'fontloader'
const DATA_KEY = `opp.${NAME}`
const JQUERY_NO_CONFLICT = $.fn[NAME]

const RE_QUOTES = /["']/g

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class FontLoader {
  constructor(element) {
    this._document = element ? (
      element.nodeType === 9 ? element : element.ownerDocument
    ) : (
      document
    )
    this._fonts = this._document.fonts
    this._state = {}
  }

  _getState(font) {
    return this._state[font.id]
  }

  _setState(font, state) {
    this._state[font.id] = state
  }

  _hasFontFace({ family, style, weight }) {
    if (this._fonts) {
      // N.B. The provided font family may include one or more font family
      // names separated by commas. Each font family name may or may not be
      // quoted. Split string on commas and remove quotes before comparison to
      // check first font name only.
      const _family = (family.split(',', 1)[0]).replace(RE_QUOTES, '')
      return [...this._fonts].some(ff => {
        if (_family === ff.family.replace(RE_QUOTES, '')) {
          // N.B. weight may be a number or a string, use `==`.
          return ff.style === style && ff.weight == weight // eslint-disable-line eqeqeq
        }

        return false
      })
    }
  }

  _hasLinkElem({ uri }) {
    Boolean(this._document.head.querySelector(`link[rel="stylesheet"][href^="${uri}"]`))
  }

  _maybeLoaded(font) {
    return this._fonts ?
      this._hasFontFace(font) :
      this._hasLinkElem(font)
  }

  _shouldLoad(font, chars) {
    const fontState = this._getState(font)

    if (fontState) {
      // Already loaded, nothing to do.
      if (!fontState.chars || isSuperset(fontState.chars, chars)) {
        return false
      }

      // Already loaded, but missing one or more required character.
      return true
    }

    // Already been loaded by other means, do not load.
    if (this._maybeLoaded(font)) {
      return false
    }

    // Not loaded.
    return true
  }

  // Public

  load(font, text, target) {
    if (!font) {
      return
    }

    const { family, style, weight, uri } = font
    if (!family || !style || !weight || !uri) {
      return
    }

    const _target = target || this._document.head

    const chars = text && charsOfText(text)
    if (!this._shouldLoad(font, chars)) {
      return
    }

    if (chars) {
      const fontState = this._getState(font)
      if (fontState && fontState.chars) {
        for (const ch of fontState.chars) {
          chars.add(ch)
        }
      }
    }

    this._setState(font, { chars })

    let query = '&display=swap'
    if (chars) {
      query += `&text=${encodeChars(chars)}`
    }

    const link = this._document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'
    link.href = String(uri) + query

    _target.appendChild(link)

    return true
  }

  static _jQueryInterface(config, ...args) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)

      if (!data) {
        data = new FontLoader(this)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = FontLoader._jQueryInterface
$.fn[NAME].Constructor = FontLoader
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return FontLoader._jQueryInterface
}

export default FontLoader
